<template>
  <div class="editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <router-link
          :to="{ name: 'pdf_report_editor', params: { report_id: reportId } }">
          <b-button
            type="is-light"
            class="back-btn mr-5"
            icon-right="arrow-left" />
        </router-link>
        <div class="report-breadcrumbs">
          <div>
            <h4>Report</h4>
            <h2>{{ reportData?.name || "Pdf Report" }}</h2>
          </div>

          <div class="arrow-right">
            <i class="material-icons">chevron_right </i>
          </div>

          <div>
            <h4>Module</h4>
            <h2>{{ module?.title || moduleConfigs.name }}</h2>
          </div>
        </div>
      </div>

      <div class="controls">
        <a-button
          type="button ml-2"
          :disabled="!isDirty || hasInvalidDaypart"
          :loading="isSaving"
          hotkeys="ctrl+shift+s"
          class="button is-primary"
          @click="save">
          Publish Changes
        </a-button>
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          hotkeys="ctrl+shift+d"
          class="button is-light"
          @click="getData">
          Discard Changes
        </a-button>
      </div>
    </div>
    <div class="editor-body">
      <div class="editor-and-preview">
        <splitpanes class="default-theme">
          <pane
            min-size="20"
            class="pane">
            <div
              v-if="module"
              class="block editor p-5">
              <p class="module-note mb-5">
                Shows weather for every location with a weather integration. In a
                location-scoped email it narrows to that location.
              </p>

              <b-field
                label="Title"
                class="input-field"
                message="Names the module in this report's module list.">
                <b-input
                  v-model="module.title" />
              </b-field>

              <b-field
                label="Card type"
                class="input-field"
                :message="layoutHelp">
                <b-select
                  v-model="module.weather_style.layout"
                  expanded>
                  <option
                    v-for="option in layoutOptions"
                    :key="option.value"
                    :value="option.value">
                    {{ option.text }}
                  </option>
                </b-select>
              </b-field>

              <TableTimeRange
                v-if="module.weather_style.layout === 'date_range'"
                v-model="module.default_time_range"
                :report-default-time-range="reportData?.default_time_range"
                class="input-field" />

              <b-field
                v-if="module.weather_style.layout === 'compact'"
                label="Cards per row"
                class="input-field"
                message="Emails show at most 2 per row so cards stay readable on a phone.">
                <b-numberinput
                  v-model="module.weather_style.columns_per_row"
                  :min="1"
                  :max="8"
                  controls-alignment="right"
                  expanded />
              </b-field>

              <template v-if="module.weather_style.layout === 'daypart'">
                <label class="label">Dayparts</label>
                <div
                  v-for="(daypart, index) in module.dayparts"
                  :key="daypart.id || index"
                  class="daypart-row">
                  <b-field
                    class="daypart-label"
                    :type="daypart.label ? '' : 'is-warning'">
                    <b-input
                      v-model="daypart.label"
                      placeholder="Label, e.g. Lunch" />
                  </b-field>
                  <b-field class="daypart-hour">
                    <b-select
                      v-model="daypart.start_hour"
                      expanded>
                      <option
                        v-for="option in hourOptions"
                        :key="option.value"
                        :value="option.value">
                        {{ option.text }}
                      </option>
                    </b-select>
                  </b-field>
                  <span class="daypart-to">to</span>
                  <b-field class="daypart-hour">
                    <b-select
                      v-model="daypart.end_hour"
                      expanded>
                      <option
                        v-for="option in hourOptions"
                        :key="option.value"
                        :value="option.value">
                        {{ option.text }}
                      </option>
                    </b-select>
                  </b-field>
                  <b-button
                    type="is-light"
                    icon-right="delete"
                    aria-label="Remove daypart"
                    @click="removeDaypart(index)" />
                  <p
                    v-if="daypart.end_hour < daypart.start_hour"
                    class="daypart-error">
                    Ends before it starts. A daypart can't cross midnight.
                  </p>
                </div>

                <b-button
                  type="is-light"
                  icon-left="plus"
                  class="mt-2"
                  @click="addDaypart">
                  Add daypart
                </b-button>
              </template>
            </div>
          </pane>
          <pane min-size="20">
            <PreviewAndJSONEditor
              v-model="module"
              type="module"
              :full-report="reportData"
              :default-time-range="reportData?.default_time_range" />
          </pane>
        </splitpanes>
      </div>
    </div>
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="false" />

    <b-modal
      v-model="showUnsavedModal"
      :destroy-on-hide="true"
      trap-focus
      close-button-aria-label="Close"
      aria-modal
      custom-class="unsaved-modal-simple">
      <div class="unsaved-modal-content">
        <div class="unsaved-modal-title-row">
          <span class="unsaved-modal-title">You have unsaved changes</span>
        </div>
        <div class="unsaved-modal-body">
          <p>Do you want to save your changes before leaving this page?</p>
        </div>
        <div class="unsaved-modal-actions custom-unsaved-modal-actions">
          <b-button
            type="is-outlined"
            size="is-small"
            class="cancel-btn"
            @click="closeUnsavedModal">
            Cancel
          </b-button>
          <div class="right-actions">
            <b-button
              type="is-muted"
              size="is-small"
              class="discard-btn"
              @click="discardAndNavigate">
              Discard
            </b-button>
            <b-button
              size="is-small"
              class="save-btn"
              @click="saveAndNavigate">
              Save Changes
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import uniqid from 'uniqid'
  import { Splitpanes, Pane } from 'splitpanes'
  import PreviewAndJSONEditor from '@/components/pdf_report/PreviewAndJsonEditor.vue'
  import TableTimeRange from '@/components/pdf_report/table_module/table_settings/tabs/TableTimeRange.vue'

  const DEFAULT_WEATHER_STYLE = {
    layout: 'card',
    columns_per_row: 4,
  }

  const DEFAULT_DAYPARTS = [
    { label: 'Lunch', start_hour: 11, end_hour: 14, },
    { label: 'Dinner', start_hour: 17, end_hour: 20, },
  ]

  const LAYOUTS = [
    {
      value: 'card',
      text: 'Detailed card',
      help: 'The reported day and the next day\'s forecast, one card per location. Best for a location-scoped email.',
    },
    {
      value: 'compact',
      text: 'Compact cards',
      help: 'A small card per location, for reports covering several.',
    },
    {
      value: 'daypart',
      text: 'Dayparts',
      help: 'Each daypart\'s high, low and worst condition for the reported day and the next. Hours are each location\'s local time and both ends are included, so 11am–2pm reads 11, 12, 1 and 2.',
    },
    {
      value: 'date_range',
      text: 'Date range',
      help: 'The reported day, then every date in the time range below (the report\'s unless you pick one): the actual where there is one, the forecast where not, and blank where neither.',
    },
  ]

  function formatHour (hour) {
    const suffix = hour < 12 ? 'am' : 'pm'
    return `${hour % 12 || 12}${suffix}`
  }

  export default {
    name: 'WeatherEditor',
    components: {
      Splitpanes,
      Pane,
      PreviewAndJSONEditor,
      TableTimeRange,
    },

    beforeRouteLeave (to, from, next) {
      if (this.isDirty) {
        this.showUnsavedModal = true
        this.nextRoute = to
      } else {
        next()
      }
    },
    data () {
      return {
        moduleId: null,
        reportId: null,
        module: null,
        reportData: null,

        moduleConfigs: {
          name: 'Weather',
          type: 'WeatherModule',
          icon: require('@/assets/icons/weather.svg'),
        },

        isLoading: false,
        isDirty: false,
        isSaving: false,

        showUnsavedModal: false,
        nextRoute: null,
      }
    },

    computed: {
      layoutOptions () {
        return LAYOUTS
      },
      layoutHelp () {
        const layout = LAYOUTS.find((option) => option.value === this.module?.weather_style?.layout)
        return (layout || LAYOUTS[0]).help
      },
      hourOptions () {
        return Array.from({ length: 24, }, (_, hour) => ({ value: hour, text: formatHour(hour), }))
      },
      hasInvalidDaypart () {
        if (this.module?.weather_style?.layout !== 'daypart') {
          return false
        }
        return (this.module.dayparts || []).some((d) => d.end_hour < d.start_hour)
      },
    },

    watch: {
      module: {
        handler () {
          this.isDirty = true
        },
        deep: true,
      },
    },

    async mounted () {
      this.moduleId = this.$route.params.module_id
      this.reportId = this.$route.params.report_id

      await this.getData()
      this.isLoading = false
      if (!this.module.key) {
        this.module.key = uniqid()
      }

      await this.$store.dispatch('app/reportsConfig/refreshOptions')
    },

    methods: {
      async getData () {
        this.isLoading = true
        const response = await this.$apis.pdfReports.getPdfModuleData(
          this.moduleId
        )
        // A module saved before `weather_style` existed would otherwise leave
        // the layout controls bound to nothing.
        response.weather_style = { ...DEFAULT_WEATHER_STYLE, ...response.weather_style, }
        // Set before the module becomes reactive: Vue 2 cannot see a property
        // added later, so a list bound to a missing key would never update.
        if (!Array.isArray(response.dayparts)) {
          response.dayparts = DEFAULT_DAYPARTS.map((d) => ({ ...d, }))
        }
        if (!('default_time_range' in response)) {
          response.default_time_range = null
        }
        this.module = response

        this.reportData = await this.$apis.pdfReports.getPdfReportData(
          this.reportId
        )

        await this.$nextTick()
        this.isDirty = false
        this.isLoading = false
      },

      addDaypart () {
        this.module.dayparts.push({ label: '', start_hour: 11, end_hour: 14, })
      },

      removeDaypart (index) {
        this.module.dayparts.splice(index, 1)
      },

      async save () {
        if (this.hasInvalidDaypart) {
          this.$buefy.toast.open({
            message: 'A daypart ends before it starts. Fix it before publishing.',
            type: 'is-warning',
          })
          return
        }
        this.isSaving = true
        this.isDirty = false
        const response = await this.$apis.pdfReports.updatePdfReportModule(this.module)
        this.isSaving = false

        if (!response) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Backend Error, Report this to the devs',
            type: 'is-warning',
          })
          return
        }

        if (response?.errors) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Error saving module, Fix the errors and try again',
            type: 'is-warning',
          })
        } else {
          this.module = response
          this.$buefy.toast.open({
            message: 'Changes Saved',
            type: 'is-success',
            position: 'is-bottom',
          })
          await this.$nextTick()
          this.isDirty = false
        }
      },

      closeUnsavedModal () {
        this.showUnsavedModal = false
        this.nextRoute = null
      },
      discardAndNavigate () {
        this.isDirty = false
        this.showUnsavedModal = false
        if (this.nextRoute) {
          this.$router.push(this.nextRoute)
        }
      },
      async saveAndNavigate () {
        await this.save()
        this.showUnsavedModal = false
        if (this.nextRoute) {
          this.$router.push(this.nextRoute)
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }

  .arrow-right {
    display: flex;
    align-items: center;
    margin: 0 10px;
    font-size: 20px;
    color: #b0b0b0;
  }
}
.back-btn {
  font-size: 20px !important;
  border-radius: 100% !important;
}
.editor-container {
  padding: 20px;
  padding-top: 5px;
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.editor-body {
  display: flex;
  height: 100%;
  overflow: hidden;
}

.block {
  border: $border-1;
  border-radius: 3px;
  height: 100%;
  flex-direction: column;
  display: flex;

  &.editor {
    width: 100%;
  }
}

.daypart-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .field {
    margin-bottom: 0;
  }
  .daypart-label {
    flex: 1 1 160px;
  }
  .daypart-hour {
    flex: 0 0 110px;
  }
  .daypart-to {
    font-size: 13px;
    color: $grayscale-3;
  }
  .daypart-error {
    flex-basis: 100%;
    font-size: 12px;
    color: $red;
    margin: 0;
  }
}

.module-note {
  font-size: 13px;
  color: $grayscale-3;
  max-width: 60ch;
}

.editor-and-preview {
  width: 100%;
}

.editor-header {
  margin-bottom: 20px;

  display: flex;
  padding-top: 10px;
  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    align-items: center;
  }

  .controls {
    margin-left: auto;
    display: flex;
    align-items: center;

    .button {
      padding: 0 15px;
      font-size: 12px;
    }
  }
}

.unsaved-modal-simple {
  .modal-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
.unsaved-modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px 24px 16px 24px;
  min-width: 320px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.unsaved-modal-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.unsaved-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
}
.unsaved-modal-body {
  font-size: 15px;
  color: #444;
  margin-bottom: 18px;
}
.custom-unsaved-modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0;
  padding-top: 4px;
}
.custom-unsaved-modal-actions .cancel-btn {
  min-width: 90px;
  border-radius: 4px !important;
  color: black !important;
  font-size: 14px !important;
  border: 1px solid #e0e0e0 !important;
}
.custom-unsaved-modal-actions .right-actions {
  display: flex;
  gap: 8px;
}
.custom-unsaved-modal-actions .discard-btn {
  background: #f5f5f5 !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
.custom-unsaved-modal-actions .save-btn {
  background: #111 !important;
  color: #fff !important;
  border: 1px solid #111 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
</style>
